import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { formatDate, mostRecentSubscriptions } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CreditCard, MapPin, Users, Car, TrendingUp, Clock, Download, Gift, UserPlus, Ticket, Ban, Undo2, Award, Droplets, CircleSlash } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#0284C7', '#06B6D4', '#10B981', '#F59E0B', '#EF4444'];

const Dashboard = () => {
  const { t, language } = useLanguage();
  const { isAdmin } = useAuth();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLocations: 0,
    totalSubscriptions: 0,
    activeSubscriptions: 0,
    expiredSubscriptions: 0,
    subscriptionTypes: [],
    recentSubscriptions: []
  });
  // Campaign totals from GET /admin/referrals/metrics. Counts only — the endpoint
  // deliberately exposes no referrer→referred pairing, and neither does this page: who
  // invited whom is the most sensitive thing the feature stores and it is not needed to
  // judge how the campaign is doing.
  const [referralMetrics, setReferralMetrics] = useState(null);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (isAdmin) {
      fetchAdminStats();
      fetchReferralMetrics();
    } else {
      setLoading(false);
    }
  }, [isAdmin]);

  const fetchAdminStats = async () => {
    try {
      const [usersRes, locationsRes, subscriptionsRes, typesRes] = await Promise.all([
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/subscription-types`)
      ]);

      const subscriptions = subscriptionsRes.data;
      const now = new Date();
      
      const activeCount = subscriptions.filter(s => 
        s.subscription.status === 'active' && new Date(s.subscription.end_date) > now
      ).length;
      
      const expiredCount = subscriptions.filter(s => 
        s.subscription.status === 'expired' || new Date(s.subscription.end_date) <= now
      ).length;

      // Count subscriptions by type
      const typeCount = {};
      subscriptions.forEach(s => {
        const typeName = s.subscription_type_name;
        typeCount[typeName] = (typeCount[typeName] || 0) + 1;
      });

      const subscriptionsByType = Object.entries(typeCount).map(([name, count]) => ({
        name,
        count
      }));

      setAllSubscriptions(subscriptions);
      setStats({
        totalUsers: usersRes.data.length,
        totalLocations: locationsRes.data.length,
        totalSubscriptions: subscriptions.length,
        activeSubscriptions: activeCount,
        expiredSubscriptions: expiredCount,
        subscriptionTypes: subscriptionsByType,
        recentSubscriptions: mostRecentSubscriptions(subscriptions, 5)
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetched on its own, not inside the Promise.all above: a backend that predates the
  // referral endpoints answers 404 here, and rejecting the whole batch would blank the
  // entire dashboard over one optional section.
  const fetchReferralMetrics = async () => {
    try {
      const res = await axios.get(`${API}/admin/referrals/metrics`);
      setReferralMetrics(res.data);
    } catch (error) {
      console.error('Error fetching referral metrics:', error);
    }
  };

  const handleExport = () => {
    setExporting(true);
    try {
      const wb = XLSX.utils.book_new();
      const isRo = language === 'ro';

      // Sheet 1: Sumar
      const summaryData = [
        [isRo ? 'Indicator' : 'Metric', isRo ? 'Valoare' : 'Value'],
        [isRo ? 'Total utilizatori' : 'Total users', stats.totalUsers],
        [isRo ? 'Total locații' : 'Total locations', stats.totalLocations],
        [isRo ? 'Abonamente active' : 'Active subscriptions', stats.activeSubscriptions],
        [isRo ? 'Abonamente expirate' : 'Expired subscriptions', stats.expiredSubscriptions],
        [isRo ? 'Total abonamente' : 'Total subscriptions', stats.totalSubscriptions],
      ];
      const wsSum = XLSX.utils.aoa_to_sheet(summaryData);
      wsSum['!cols'] = [{ wch: 30 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsSum, isRo ? 'Sumar' : 'Summary');

      // Sheet 2: Distribuție pe tip
      const typeData = [
        [isRo ? 'Tip abonament' : 'Subscription type', isRo ? 'Număr' : 'Count'],
        ...stats.subscriptionTypes.map(t => [t.name, t.count]),
      ];
      const wsTypes = XLSX.utils.aoa_to_sheet(typeData);
      wsTypes['!cols'] = [{ wch: 30 }, { wch: 15 }];
      XLSX.utils.book_append_sheet(wb, wsTypes, isRo ? 'Pe tip' : 'By type');

      // Sheet 3: Toate abonamentele
      const now = new Date();
      const subRows = allSubscriptions.map(s => {
        const end = new Date(s.subscription.end_date);
        const isActive = s.subscription.status === 'active' && end > now;
        return {
          [isRo ? 'Utilizator' : 'User']: s.user_name,
          [isRo ? 'Telefon' : 'Phone']: s.user_phone,
          ['Email']: s.user_email,
          [isRo ? 'Nume Companie' : 'Company name']: s.user_company,
          [isRo ? 'Nr. înmatriculare' : 'License plate']: s.license_plate,
          [isRo ? 'Marcă' : 'Brand']: s.car_brand,
          [isRo ? 'Model' : 'Model']: s.car_model,
          [isRo ? 'Culoare' : 'Color']: s.car_color,
          [isRo ? 'Tip abonament' : 'Subscription type']: s.subscription_type_name,
          [isRo ? 'Status' : 'Status']: isActive ? (isRo ? 'Activ' : 'Active') : (isRo ? 'Expirat' : 'Expired'),
          [isRo ? 'Spălări rămase' : 'Washes remaining']: s.subscription.washes_remaining ?? (isRo ? 'Nelimitat' : 'Unlimited'),
          [isRo ? 'Data start' : 'Start date']: formatDate(s.subscription.start_date),
          [isRo ? 'Data expirare' : 'End date']: formatDate(end),
          ['Operator']: s.operator_name || (isRo ? 'Fără recomandare' : 'No referral'),
        };
      });
      const wsSubs = XLSX.utils.json_to_sheet(subRows);
      wsSubs['!cols'] = [
        { wch: 25 }, { wch: 15 }, { wch: 28 }, { wch: 22 },
        { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 12 },
        { wch: 22 }, { wch: 12 }, { wch: 18 },
        { wch: 14 }, { wch: 14 }, { wch: 20 },
      ];
      XLSX.utils.book_append_sheet(wb, wsSubs, isRo ? 'Abonamente' : 'Subscriptions');

      // Sheet: Pe operator / By operator
      const operatorCounts = {};
      allSubscriptions.forEach(s => {
        const key = s.operator_name || (isRo ? 'Fără recomandare' : 'No referral');
        operatorCounts[key] = (operatorCounts[key] || 0) + 1;
      });

      const noReferralKey = isRo ? 'Fără recomandare' : 'No referral';
      const operatorRows = Object.entries(operatorCounts)
        .filter(([key]) => key !== noReferralKey)
        .sort((a, b) => b[1] - a[1]);
      if (operatorCounts[noReferralKey] !== undefined) {
        operatorRows.push([noReferralKey, operatorCounts[noReferralKey]]);
      }

      const operatorData = [
        ['Operator', isRo ? 'Nr. abonamente' : 'Subscriptions'],
        ...operatorRows,
      ];
      const wsOperators = XLSX.utils.aoa_to_sheet(operatorData);
      XLSX.utils.book_append_sheet(wb, wsOperators, isRo ? 'Pe operator' : 'By operator');

      const date = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `ecowash-raport-${date}.xlsx`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dashboard-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="space-y-8" data-testid="dashboard-page">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('dashboard')}</h1>
          <p className="text-muted-foreground mt-1">
            {language === 'ro' ? 'Bine ai venit!' : 'Welcome!'}
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <p className="text-muted-foreground">
              {language === 'ro' 
                ? 'Contacteaza administratorul pentru a obtine acces la panoul de administrare.'
                : 'Contact administrator to get access to the admin panel.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The counts the endpoint returns: codes, conversions, the v2 reward ledger, then the
  // legacy gift rows (0 on prod, where the campaign never ran with gifts).
  const referralCards = [
    {
      key: 'codes_issued',
      label: language === 'ro' ? 'Coduri emise' : 'Codes issued',
      icon: Ticket,
      color: 'text-primary',
    },
    {
      key: 'credited_referrals',
      label: language === 'ro' ? 'Recomandări reușite' : 'Successful referrals',
      icon: UserPlus,
      color: 'text-green-500',
    },
    {
      key: 'rewards_granted',
      label: language === 'ro' ? 'Recompense acordate' : 'Rewards granted',
      icon: Award,
      color: 'text-green-500',
    },
    {
      key: 'reward_washes_granted',
      label: language === 'ro' ? 'Spălări din recompense' : 'Reward washes',
      icon: Droplets,
      color: 'text-cyan-500',
    },
    {
      key: 'rewards_skipped',
      label: language === 'ro' ? 'Recompense neacordate' : 'Rewards not granted',
      icon: CircleSlash,
      color: 'text-muted-foreground',
    },
    {
      key: 'gift_washes_outstanding',
      label: language === 'ro' ? 'Spălări cadou nefolosite' : 'Unused gift washes',
      icon: Gift,
      color: 'text-cyan-500',
    },
    {
      key: 'gifts_revoked',
      label: language === 'ro' ? 'Cadouri revocate' : 'Gifts revoked',
      icon: Ban,
      color: 'text-amber-500',
    },
    {
      key: 'washes_revoked',
      label: language === 'ro' ? 'Spălări retrase' : 'Washes reclaimed',
      icon: Undo2,
      color: 'text-destructive',
    },
  ];

  const WASH_METRIC_KEYS = new Set(['reward_washes_granted', 'gift_washes_outstanding', 'washes_revoked']);
  const referralChartData = referralCards
    // Counts only: wash totals live on a different scale and would flatten the bars.
    .filter(({ key }) => !WASH_METRIC_KEYS.has(key))
    .map(({ key, label }) => ({
      name: label,
      count: referralMetrics?.[key] ?? 0,
    }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">{t('dashboard')}</h1>
          <p className="text-muted-foreground mt-1">
            {language === 'ro' ? 'Statistici si rapoarte' : 'Statistics and reports'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExport}
          disabled={exporting || stats.totalSubscriptions === 0}
          className="gap-2"
        >
          <Download className="w-4 h-4" />
          {language === 'ro' ? 'Export Excel' : 'Export Excel'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover" data-testid="stat-total-users">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('total_users')}
            </CardTitle>
            <Users className="w-5 h-5 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalUsers}</div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-total-locations">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('total_locations')}
            </CardTitle>
            <MapPin className="w-5 h-5 text-cyan-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalLocations}</div>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-active-subscriptions">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('active_subscriptions')}
            </CardTitle>
            <TrendingUp className="w-5 h-5 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.activeSubscriptions}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.expiredSubscriptions} {t('expired').toLowerCase()}
            </p>
          </CardContent>
        </Card>

        <Card className="card-hover" data-testid="stat-total-subscriptions">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t('total_subscriptions')}
            </CardTitle>
            <CreditCard className="w-5 h-5 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.totalSubscriptions}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Subscriptions by Type */}
        <Card className="card-hover" data-testid="chart-by-type">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t('subscriptions_by_type')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.subscriptionTypes.length > 0 ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={stats.subscriptionTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="count"
                      label={({ name, count }) => `${name}: ${count}`}
                    >
                      {stats.subscriptionTypes.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t('no_subscriptions')}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Subscriptions */}
        <Card className="card-hover" data-testid="recent-subscriptions">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t('recent_subscriptions')}</CardTitle>
          </CardHeader>
          <CardContent>
            {stats.recentSubscriptions.length > 0 ? (
              <div className="space-y-4">
                {stats.recentSubscriptions.map((sub, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Car className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{sub.user_name}</p>
                        <p className="text-sm text-muted-foreground">{sub.license_plate}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{sub.subscription_type_name}</p>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(sub.subscription.end_date)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                {t('no_subscriptions')}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Referral campaign — counts only, never a referrer↔referred list */}
      {referralMetrics && (
        <div className="space-y-6">
          <div>
            <h2 className="text-xl font-bold font-['Manrope'] tracking-tight">
              {language === 'ro' ? 'Campanie recomandări' : 'Referral campaign'}
            </h2>
            <p className="text-muted-foreground text-sm mt-1">
              {language === 'ro'
                ? 'Totaluri pe toată campania. Cine pe cine a recomandat nu se afișează aici.'
                : 'Campaign-wide totals. Who referred whom is not shown here.'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {referralCards.map(({ key, label, icon: Icon, color }) => (
              <Card className="card-hover" key={key} data-testid={`stat-referral-${key}`}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {label}
                  </CardTitle>
                  <Icon className={`w-5 h-5 ${color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{referralMetrics[key] ?? 0}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card className="card-hover" data-testid="chart-referrals">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">
                {language === 'ro' ? 'Recomandări — pe indicator' : 'Referrals — by metric'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={referralChartData}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" name="Total" radius={[4, 4, 0, 0]}>
                      {referralChartData.map((entry, index) => (
                        <Cell key={`referral-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
};

export default Dashboard;
